import PaginationModel from "@/models/paginationModel";

import ConfiguracaoRotuloModel from "../models/configuracaoRotuloModel";
import OrigemRotulo from "../models/enum/rotulo/origemRotulo";
import nfetch from "../utils/events/nfetch";

import { iServiceCached } from "./base/iServiceCached";

function mapCombo(dados: ConfiguracaoRotuloModel[]) {
    return dados.map(p => ({
        value: p.id,
        text: p.nome,
        origemDados: p.origemDados,
    }));
}

class ConfiguracaoRotuloService extends iServiceCached<ConfiguracaoRotuloModel> {
    constructor() {
        super("RotuloPersonalizado");
    }

    public replicar(configuracaoRotuloId: number, idFranquias: string): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/Replicar?configuracaoRotuloId=${configuracaoRotuloId}&idFranquias=${idFranquias}`,
            {
                method: "post",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json; charset=UTF-8",
                },
            },
        );
    }

    public copiar(configuracaoRotuloId: number): Promise<Response> {
        return nfetch(`/${this.controllerName}/Copiar?configuracaoRotuloId=${configuracaoRotuloId}`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
        });
    }

    public getCamposTabelaLote(): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetCamposTabelaLote`, {
            method: "get",
            credentials: "same-origin",
        });
    }

    public getCamposTabelaManipulacao(): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetCamposTabelaManipulacao`, {
            method: "get",
            credentials: "same-origin",
        });
    }

    public getRotuloLote(loteId: number, rotuloId = 0): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetRotuloLote?id=${loteId}&rotuloId=${rotuloId}`, {
            method: "get",
            credentials: "same-origin",
        });
    }

    public getRotuloManipulacao(manipulacaoId: number, rotuloId = 0): Promise<Response> {
        return nfetch(`/${this.controllerName}/GetRotuloManipulacao?id=${manipulacaoId}&rotuloId=${rotuloId}`, {
            method: "get",
            credentials: "same-origin",
        });
    }

    public salvarRotulo(rotulos: ConfiguracaoRotuloModel[]): Promise<Response> {
        return nfetch(`/${this.controllerName}/SalvarRotulo`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify(rotulos),
        });
    }

    public async comboCachedManipulacao(franquiaId: number) {
        let retorno = await this.combo(franquiaId);
        retorno = retorno.filter(
            item =>
                item.origemDados == OrigemRotulo.Manipulacao ||
                item.origemDados == OrigemRotulo.Acabado ||
                item.origemDados == OrigemRotulo.AcabadoProprio ||
                item.origemDados == OrigemRotulo.Strips,
        );

        return mapCombo(retorno);
    }

    public async comboCachedByFranquia(franquiaId: number) {
        if (this.cached[franquiaId]) {
            return mapCombo(this.cached[franquiaId]);
        }

        const response = await nfetch(`/${this.controllerName}/ListByFranquia?franquiaId=${franquiaId}`, {
            credentials: "same-origin",
        })
            .withLoading()
            .resolveWithJSON<PaginationModel<ConfiguracaoRotuloModel>>();

        this.cached[franquiaId] = response.list;

        return mapCombo(response.list);
    }

    public async comboCachedByFranquiaManipulacao(franquiaId: number) {
        const retorno = await this.comboCachedByFranquia(franquiaId);
        return retorno.filter(
            item =>
                item["origemDados"] == OrigemRotulo.Manipulacao ||
                item["origemDados"] == OrigemRotulo.Acabado ||
                item["origemDados"] == OrigemRotulo.AcabadoProprio ||
                item["origemDados"] == OrigemRotulo.Strips,
        );
    }

    public async comboCached(franquiaId: number) {
        const retorno = await this.combo(franquiaId);
        return mapCombo(retorno);
    }

    public getRotulos(
        lotesIds: number[] = [],
        manipulacoesIds: number[] = [],
        vendaId = 0,
        origemRotulo: OrigemRotulo,
    ): Promise<Response> {
        return nfetch(
            `/${this.controllerName}/GetRotulos?lotesIds=${lotesIds}&manipulacoesIds=${manipulacoesIds}&vendaId=${vendaId}&origemRotulo=${origemRotulo}`,
            {
                method: "post",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json; charset=UTF-8",
                },
                body: JSON.stringify({ lotesIds, manipulacoesIds }),
            },
        );
    }
}

export default new ConfiguracaoRotuloService();
